/**
 * Find Similar CVs Page
 * 
 * Upload a CV or select an existing one to find the 3 most similar CVs
 * using AI-powered semantic search.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    CircularProgress,
    LinearProgress,
    Stack,
    Grid,
    Autocomplete,
    TextField,
    Divider,
    Tabs,
    Tab,
    Chip
} from '@mui/material';
import {
    UploadFile as UploadFileIcon,
    Search as SearchIcon,
    Person as PersonIcon,
    Refresh as RefreshIcon,
    CompareArrows as CompareArrowsIcon
} from '@mui/icons-material';
import CVMatchingResults from '../components/CVMatchingResults';
// CVSourceToggle removed - only associate CVs for now
import { findSimilarCVs, FindSimilarCVsResult, searchCVsByName, ExistingCV, searchCVsByAttributes, CVAttributes } from '../services/similarCVs';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

const FindSimilarCVsPage: React.FC = () => {
    // CV Source - fixed to associate only for now
    const cvSource = 'associate' as const;
    
    // Tab state: 0 = Select Existing, 1 = Upload New
    const [tabValue, setTabValue] = useState(0);
    
    // Existing CV selection - search as you type
    const [cvOptions, setCvOptions] = useState<ExistingCV[]>([]);
    const [selectedCV, setSelectedCV] = useState<ExistingCV | null>(null);
    const [isLoadingCVs, setIsLoadingCVs] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    
    // Upload state
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    
    // Search state
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [results, setResults] = useState<FindSimilarCVsResult | null>(null);
    
    // Selected attributes for refined search
    const [selectedAttributes, setSelectedAttributes] = useState<{
        skills: Set<string>;
        sectors: Set<string>;
        geographic_experience: Set<string>;
        languages: Set<string>;
        methodologies: Set<string>;
        tools: Set<string>;
    }>({
        skills: new Set(),
        sectors: new Set(),
        geographic_experience: new Set(),
        languages: new Set(),
        methodologies: new Set(),
        tools: new Set()
    });
    
    // Track if we're doing attribute-based search
    const [isAttributeSearching, setIsAttributeSearching] = useState(false);
    
    // Track if user has performed the attribute search (to show "no results" only after searching)
    const [hasSearchedWithAttributes, setHasSearchedWithAttributes] = useState(false);
    
    // Search CVs as user types (debounced)
    useEffect(() => {
        if (searchInput.length < 2) {
            setCvOptions([]);
            return;
        }
        
        const timeoutId = setTimeout(async () => {
            setIsLoadingCVs(true);
            try {
                const cvs = await searchCVsByName(searchInput, cvSource);
                setCvOptions(cvs);
            } catch (error) {
                console.error('Failed to search CVs:', error);
            } finally {
                setIsLoadingCVs(false);
            }
        }, 300); // 300ms debounce
        
        return () => clearTimeout(timeoutId);
    }, [searchInput, cvSource]);
    
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.name.toLowerCase().endsWith('.pdf')) {
                setSearchError('Please select a PDF file');
                return;
            }
            setSelectedFile(file);
            setSearchError(null);
            setResults(null);
        }
    };
    
    // Step 1: Extract attributes from CV (no similar CV search yet)
    const handleExtractAttributes = async () => {
        setIsSearching(true);
        setSearchError(null);
        setResults(null);
        setUploadProgress(0);
        setHasSearchedWithAttributes(false);
        
        try {
            let result: FindSimilarCVsResult;
            
            if (tabValue === 0 && selectedCV) {
                // Extract attributes from existing CV
                setUploadProgress(30);
                result = await findSimilarCVs({
                    cv_id: selectedCV.id,
                    cv_source: cvSource,
                    top_k: 0  // Don't search for similar CVs yet, just extract attributes
                });
            } else if (tabValue === 1 && selectedFile) {
                // Extract attributes from uploaded CV
                setUploadProgress(20);
                
                // Read file content
                const text = await extractTextFromPDF(selectedFile);
                setUploadProgress(50);
                
                result = await findSimilarCVs({
                    cv_content: text,
                    cv_source: cvSource,
                    top_k: 0  // Don't search for similar CVs yet, just extract attributes
                });
            } else {
                throw new Error('Please select or upload a CV first');
            }
            
            setUploadProgress(100);
            // Store source CV info but clear any matches (we'll search after attribute selection)
            setResults({
                ...result,
                matches: [],
                total_found: 0
            });
            
        } catch (error: any) {
            console.error('Attribute extraction failed:', error);
            setSearchError(error.message || 'Failed to extract attributes');
        } finally {
            setIsSearching(false);
        }
    };
    
    const handleReset = () => {
        setSelectedCV(null);
        setSelectedFile(null);
        setResults(null);
        setSearchError(null);
        setUploadProgress(0);
        setHasSearchedWithAttributes(false);
        setSelectedAttributes({
            skills: new Set(),
            sectors: new Set(),
            geographic_experience: new Set(),
            languages: new Set(),
            methodologies: new Set(),
            tools: new Set()
        });
    };
    
    const toggleAttribute = (category: keyof typeof selectedAttributes, value: string) => {
        setSelectedAttributes(prev => {
            const newSet = new Set(prev[category]);
            if (newSet.has(value)) {
                newSet.delete(value);
            } else {
                newSet.add(value);
            }
            return { ...prev, [category]: newSet };
        });
    };
    
    const selectAllInCategory = (category: keyof typeof selectedAttributes, values: string[]) => {
        setSelectedAttributes(prev => ({
            ...prev,
            [category]: new Set(values)
        }));
    };
    
    const deselectAllInCategory = (category: keyof typeof selectedAttributes) => {
        setSelectedAttributes(prev => ({
            ...prev,
            [category]: new Set()
        }));
    };
    
    const getTotalSelectedCount = () => {
        return Object.values(selectedAttributes).reduce((sum, set) => sum + set.size, 0);
    };
    
    const handleSearchWithSelected = async () => {
        const totalSelected = getTotalSelectedCount();
        if (totalSelected === 0) {
            setSearchError('Please select at least one attribute to search');
            return;
        }
        
        setIsAttributeSearching(true);
        setSearchError(null);
        setHasSearchedWithAttributes(true);
        
        try {
            const attributesToSearch: Partial<CVAttributes> = {
                skills: Array.from(selectedAttributes.skills),
                sectors: Array.from(selectedAttributes.sectors),
                geographic_experience: Array.from(selectedAttributes.geographic_experience),
                languages: Array.from(selectedAttributes.languages),
                methodologies: Array.from(selectedAttributes.methodologies),
                tools: Array.from(selectedAttributes.tools)
            };
            
            const result = await searchCVsByAttributes({
                attributes: attributesToSearch,
                cv_source: cvSource,
                top_k: 5,
                exclude_cv_id: selectedCV?.id
            });
            
            setResults(prev => prev ? {
                ...prev,
                matches: result.matches,
                total_found: result.total_found
            } : null);
            
        } catch (error: any) {
            console.error('Attribute search failed:', error);
            setSearchError(error.message || 'Search failed');
        } finally {
            setIsAttributeSearching(false);
        }
    };
    
    // Helper to extract text from PDF (simplified - actual extraction happens server-side)
    const extractTextFromPDF = async (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    // For now, we'll send the base64 content and let the server handle PDF parsing
                    // In a production app, you might use pdf.js on the client
                    const base64 = (e.target?.result as string).split(',')[1];
                    resolve(base64);
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };
    
    const canSearch = (tabValue === 0 && selectedCV) || (tabValue === 1 && selectedFile);
    
    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CompareArrowsIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
                <Typography variant="h4" component="h1">
                    Find Similar CVs
                </Typography>
            </Box>
            
            {/* CV Source Toggle - removed, using associate CVs only */}
            
            {/* Step 1: Input Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    Step 1: Select or Upload a CV
                </Typography>
                <Tabs 
                    value={tabValue} 
                    onChange={(_, newValue) => {
                        setTabValue(newValue);
                        setResults(null);
                        setSearchError(null);
                    }}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label="Select Existing CV" icon={<PersonIcon />} iconPosition="start" />
                    <Tab label="Upload New CV" icon={<UploadFileIcon />} iconPosition="start" />
                </Tabs>
                
                {/* Tab 0: Select Existing CV */}
                <TabPanel value={tabValue} index={0}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Type at least 2 characters to search for a CV by name.
                    </Typography>
                    
                    <Autocomplete
                        options={cvOptions}
                        getOptionLabel={(option) => option.associate_name}
                        value={selectedCV}
                        inputValue={searchInput}
                        onInputChange={(_, newInputValue) => {
                            setSearchInput(newInputValue);
                        }}
                        onChange={(_, newValue) => {
                            setSelectedCV(newValue);
                            setResults(null);
                        }}
                        loading={isLoadingCVs}
                        filterOptions={(x) => x} // Disable client-side filtering - server does it
                        noOptionsText={searchInput.length < 2 ? "Type at least 2 characters to search" : "No CVs found"}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search for a CV by name"
                                placeholder="e.g., Smith, John, Brown..."
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isLoadingCVs ? <CircularProgress size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <Box>
                                    <Typography variant="body1">{option.associate_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.folder_name}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        sx={{ mb: 2 }}
                    />
                    
                    {selectedCV && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Selected: <strong>{selectedCV.associate_name}</strong> ({selectedCV.folder_name})
                        </Alert>
                    )}
                </TabPanel>
                
                {/* Tab 1: Upload New CV */}
                <TabPanel value={tabValue} index={1}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload a CV (PDF) to find similar profiles in the Associate database.
                    </Typography>
                    
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFileIcon />}
                        sx={{ mb: 2 }}
                    >
                        Select PDF File
                        <input
                            type="file"
                            hidden
                            accept=".pdf"
                            onChange={handleFileSelect}
                        />
                    </Button>
                    
                    {selectedFile && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Selected file: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(1)} KB)
                        </Alert>
                    )}
                </TabPanel>
                
                <Divider sx={{ my: 2 }} />
                
                {/* Action Buttons */}
                <Stack direction="row" spacing={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={isSearching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                        onClick={handleExtractAttributes}
                        disabled={!canSearch || isSearching}
                    >
                        {isSearching ? 'Extracting...' : 'Extract Attributes'}
                    </Button>
                    
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleReset}
                        disabled={isSearching}
                    >
                        Reset
                    </Button>
                </Stack>
                
                {/* Progress Bar */}
                {isSearching && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            {uploadProgress < 50 ? 'Processing CV...' : 'Extracting attributes...'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Error Display */}
            {searchError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {searchError}
                </Alert>
            )}
            
            {/* Source CV Attributes - Selectable Chips */}
            {results?.source_cv?.attributes && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Source CV: {results.source_cv.associate_name || results.source_cv.filename}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Click attributes to select, then search for similar CVs
                        </Typography>
                    </Box>
                    
                    <Alert severity="success" sx={{ mb: 2 }}>
                        <strong>Step 2:</strong> Select the attributes you want to search for, then click "Search with Selected" to find CVs that match ALL selected criteria.
                    </Alert>
                    
                    <Grid container spacing={2}>
                        {results.source_cv.attributes.skills?.length > 0 && (
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="subtitle2" color="primary">Skills</Typography>
                                    <Box>
                                        <Button size="small" onClick={() => selectAllInCategory('skills', results.source_cv.attributes.skills)}>All</Button>
                                        <Button size="small" onClick={() => deselectAllInCategory('skills')}>None</Button>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {results.source_cv.attributes.skills.map((skill: string) => (
                                        <Chip
                                            key={skill}
                                            label={skill}
                                            size="small"
                                            variant={selectedAttributes.skills.has(skill) ? 'filled' : 'outlined'}
                                            color={selectedAttributes.skills.has(skill) ? 'primary' : 'default'}
                                            onClick={() => toggleAttribute('skills', skill)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        )}
                        {results.source_cv.attributes.sectors?.length > 0 && (
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="subtitle2" color="primary">Sectors</Typography>
                                    <Box>
                                        <Button size="small" onClick={() => selectAllInCategory('sectors', results.source_cv.attributes.sectors)}>All</Button>
                                        <Button size="small" onClick={() => deselectAllInCategory('sectors')}>None</Button>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {results.source_cv.attributes.sectors.map((sector: string) => (
                                        <Chip
                                            key={sector}
                                            label={sector}
                                            size="small"
                                            variant={selectedAttributes.sectors.has(sector) ? 'filled' : 'outlined'}
                                            color={selectedAttributes.sectors.has(sector) ? 'primary' : 'default'}
                                            onClick={() => toggleAttribute('sectors', sector)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        )}
                        {results.source_cv.attributes.geographic_experience?.length > 0 && (
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="subtitle2" color="primary">Geographic Experience</Typography>
                                    <Box>
                                        <Button size="small" onClick={() => selectAllInCategory('geographic_experience', results.source_cv.attributes.geographic_experience)}>All</Button>
                                        <Button size="small" onClick={() => deselectAllInCategory('geographic_experience')}>None</Button>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {results.source_cv.attributes.geographic_experience.map((geo: string) => (
                                        <Chip
                                            key={geo}
                                            label={geo}
                                            size="small"
                                            variant={selectedAttributes.geographic_experience.has(geo) ? 'filled' : 'outlined'}
                                            color={selectedAttributes.geographic_experience.has(geo) ? 'primary' : 'default'}
                                            onClick={() => toggleAttribute('geographic_experience', geo)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        )}
                        {results.source_cv.attributes.languages?.length > 0 && (
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="subtitle2" color="primary">Languages</Typography>
                                    <Box>
                                        <Button size="small" onClick={() => selectAllInCategory('languages', results.source_cv.attributes.languages)}>All</Button>
                                        <Button size="small" onClick={() => deselectAllInCategory('languages')}>None</Button>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {results.source_cv.attributes.languages.map((lang: string) => (
                                        <Chip
                                            key={lang}
                                            label={lang}
                                            size="small"
                                            variant={selectedAttributes.languages.has(lang) ? 'filled' : 'outlined'}
                                            color={selectedAttributes.languages.has(lang) ? 'primary' : 'default'}
                                            onClick={() => toggleAttribute('languages', lang)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        )}
                        {results.source_cv.attributes.methodologies?.length > 0 && (
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="subtitle2" color="primary">Methodologies</Typography>
                                    <Box>
                                        <Button size="small" onClick={() => selectAllInCategory('methodologies', results.source_cv.attributes.methodologies)}>All</Button>
                                        <Button size="small" onClick={() => deselectAllInCategory('methodologies')}>None</Button>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {results.source_cv.attributes.methodologies.map((method: string) => (
                                        <Chip
                                            key={method}
                                            label={method}
                                            size="small"
                                            variant={selectedAttributes.methodologies.has(method) ? 'filled' : 'outlined'}
                                            color={selectedAttributes.methodologies.has(method) ? 'primary' : 'default'}
                                            onClick={() => toggleAttribute('methodologies', method)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        )}
                        {results.source_cv.attributes.tools?.length > 0 && (
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="subtitle2" color="primary">Tools</Typography>
                                    <Box>
                                        <Button size="small" onClick={() => selectAllInCategory('tools', results.source_cv.attributes.tools)}>All</Button>
                                        <Button size="small" onClick={() => deselectAllInCategory('tools')}>None</Button>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {results.source_cv.attributes.tools.map((tool: string) => (
                                        <Chip
                                            key={tool}
                                            label={tool}
                                            size="small"
                                            variant={selectedAttributes.tools.has(tool) ? 'filled' : 'outlined'}
                                            color={selectedAttributes.tools.has(tool) ? 'primary' : 'default'}
                                            onClick={() => toggleAttribute('tools', tool)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                    
                    {/* Search with Selected Button */}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={isAttributeSearching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                            onClick={handleSearchWithSelected}
                            disabled={getTotalSelectedCount() === 0 || isAttributeSearching}
                        >
                            {isAttributeSearching ? 'Searching...' : `Search with Selected (${getTotalSelectedCount()})`}
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                            {getTotalSelectedCount() === 0 
                                ? 'Select attributes above to enable search' 
                                : `${getTotalSelectedCount()} attribute${getTotalSelectedCount() !== 1 ? 's' : ''} selected`}
                        </Typography>
                    </Box>
                </Paper>
            )}
            
            {/* Results */}
            {results && results.matches.length > 0 && (
                <CVMatchingResults
                    matches={results.matches as any}
                    selectedCategories={results.prioritized_categories}
                />
            )}
            
            {/* No Results - only show after search completes (not while searching) */}
            {results && results.matches.length === 0 && hasSearchedWithAttributes && !isAttributeSearching && (
                <Alert severity="info">
                    No CVs found matching ALL selected attributes. Try selecting fewer attributes or different criteria.
                </Alert>
            )}
        </Box>
    );
};

export default FindSimilarCVsPage;
